import React, { useEffect, useRef, useState } from "react";

const SERVICE_OPTIONS = [
  "3D Box Letters",
  "Channel Letter",
  "ACP Board",
  "Glow Sign Board",
  "Neon Signage",
  "LED Panels",
  "Video Panel",
  "Fabric Board",
];

function useInquiryReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return [ref, visible];
}

function Reveal({ children, delayMs = 0, visible, className = "" }) {
  return (
    <div
      className={[
        visible
          ? "animate-fade-up motion-reduce:animate-none motion-reduce:opacity-100"
          : "opacity-0",
        className,
      ].join(" ")}
      style={{ animationDelay: visible ? `${delayMs}ms` : undefined }}
    >
      {children}
    </div>
  );
}

function Inquiry() {
  const [inquiryRef, visible] = useInquiryReveal();

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    service_name: "",
    details: "",
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
    setSuccess(false);
  };

  const handleFiles = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 5) {
      setError("You can upload a maximum of 5 files at a time.");
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    const totalSize = selectedFiles.reduce((acc, file) => acc + file.size, 0);
    if (totalSize > 104857600) {
      // 100MB
      setError(
        "File size more than 100MB. For larger files, send email to shyamadvertising101@gmail.com",
      );
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setFiles(selectedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.name ||
      !formData.phone ||
      !formData.service_name ||
      !formData.details
    ) {
      setError("All fields required");
      return;
    }

    setLoading(true);
    setError("");

    const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    if (!apiBase) {
      setError(
        "This build is missing REACT_APP_API_URL. Set it before npm run build.",
      );
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append("name", formData.name);
    data.append("phone", formData.phone);
    data.append("service_name", formData.service_name);
    data.append("details", formData.details);
    files.forEach((file) => data.append("files", file));

    try {
      const res = await fetch(`${apiBase}/api/shyam/inquiries`, {
        method: "POST",
        body: data,
      });

      if (res.ok) {
        setSuccess(true);
        setFormData({ name: "", phone: "", service_name: "", details: "" });
        setFiles([]);
        if (fileRef.current) fileRef.current.value = "";
      } else {
        let msg = "Submit failed";
        try {
          const raw = await res.text();
          try {
            const errData = JSON.parse(raw);
            msg = errData.message || errData.error || msg;
          } catch {
            if (raw && raw.length < 200) msg = raw;
            else if (res.status === 500) {
              msg =
                "Server error (500). Usually: run the inquiries table SQL on the API database, fix SMTP/upload paths, or check nginx/proxy logs—the HTML error page means the crash may be before Node.";
            }
          }
        } catch {
          msg = `Server returned ${res.status} but the response could not be read.`;
        }
        setError(msg);
      }
    } catch (err) {
      const detail = err?.message ? String(err.message) : "";
      const blocked =
        /failed to fetch|networkerror|load failed|network request failed/i.test(
          detail,
        );
      setError(
        blocked
          ? "The browser blocked or could not complete the request to the API (often CORS, wrong API URL in the build, or the server closed the connection). DevTools → Network shows the real status; ensure backend.aifia.in returns JSON with CORS for shyamadvertising.com."
          : `Request failed.${detail ? ` ${detail}` : ""}`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      ref={inquiryRef}
      className="relative min-h-screen flex items-center overflow-hidden bg-surface text-white py-16 lg:py-20"
    >
      <div className="absolute inset-0 bg-gradient-hero-glow" aria-hidden />
      <div
        className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,10,10,0.3)_0%,#0a0a0a_100%)]"
        aria-hidden
      />
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-2xl mx-auto">
          {success && (
            <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30 text-green-100 text-sm">
              🎉 Thank you! Your inquiry has been submitted successfully. We'll
              contact you soon.
            </div>
          )}
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-100 text-sm">
              {error}
            </div>
          )}

          <Reveal visible={visible} delayMs={400}>
            <div
              className={[
                "border-gradient-brand rounded-2xl bg-surface-elevated/90 backdrop-blur-md p-5 md:p-7 shadow-brand-glow lg:hover:shadow-[0_0_50px_-8px_rgba(234,88,12,0.35)] transition-shadow duration-500",
                visible
                  ? "animate-scale-in motion-reduce:animate-none motion-reduce:opacity-100 motion-reduce:scale-100 opacity-0"
                  : "opacity-0",
              ].join(" ")}
              style={{ animationDelay: visible ? "240ms" : undefined }}
            >
              <div className="text-center mb-5">
                <p
                  className={[
                    "text-brand-amber text-xs font-semibold uppercase tracking-wider mb-1 opacity-0",
                    visible
                      ? "motion-reduce:opacity-100 animate-fade-in motion-reduce:animate-none"
                      : "",
                  ].join(" ")}
                  style={{ animationDelay: visible ? "520ms" : undefined }}
                >
                  Instant & online
                </p>
                <h2 className="text-xl md:text-2xl font-bold text-white mb-0.5">
                  Get a free quote
                </h2>
                <p className="text-gradient-brand font-semibold text-sm">
                  Shyam Advertising
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber/50 focus:border-brand-amber/40 transition-all"
                  />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone number"
                    required
                    className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber/50 focus:border-brand-amber/40 transition-all"
                  />
                </div>

                <select
                  name="service_name"
                  value={formData.service_name}
                  onChange={handleChange}
                  required
                  className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2.5 text-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber/50 focus:border-brand-amber/40 transition-all"
                >
                  <option value="">Select a service</option>
                  {SERVICE_OPTIONS.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>

                <textarea
                  name="details"
                  value={formData.details}
                  onChange={handleChange}
                  placeholder="Project details..."
                  rows={3}
                  required
                  className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber/50 focus:border-brand-amber/40 transition-all resize-y min-h-[72px]"
                />

                <div
                  className="border border-dashed border-brand-amber/35 rounded-xl p-4 text-center cursor-pointer hover:border-brand-yellow/60 hover:bg-brand-orange/5 transition-all bg-black/30"
                  onClick={() => fileRef.current.click()}
                >
                  <p className="text-gray-400 text-xs">
                    <span className="text-brand-amber font-medium">
                      Attach files
                    </span>{" "}
                    — images, PDF, CDR (max 10MB)
                  </p>
                  <input
                    ref={fileRef}
                    type="file"
                    className="hidden"
                    multiple
                    accept="image/*,.pdf,.cdr"
                    onChange={handleFiles}
                  />
                  {files.length > 0 && (
                    <p className="text-green-400 text-xs mt-1">
                      {files.length} file(s) selected
                    </p>
                  )}
                </div>
                <span className="text-gray-400 text-xs mt-2 block text-center">
                  Max file size: 100MB. For larger files, send email to
                  shyamadvertising101@gmail.com
                </span>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-gradient-brand py-3 font-bold text-black text-sm hover:opacity-95 transition-all shadow-lg shadow-brand-orange/25 hover:shadow-[0_8px_28px_-4px_rgba(234,88,12,0.5)] active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending..." : "Send request"}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default Inquiry;
